import {
  Alert,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EmailIcon from '@mui/icons-material/Email'
import RateReviewIcon from '@mui/icons-material/RateReview'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { Dialog } from '@/components/AppDialog'
import { useFeedbackContact, useSendFeedbackRequest } from '@/api/hooks'
import type { FeedbackContact, FeedbackLinkSent } from '@/api/types'
import { ErrorBanner, InlineSpinner } from '@/components/common'

type Channel = 'EMAIL' | 'WHATSAPP'

/**
 * Asks a customer for feedback on one event. The message, its subject, the
 * address and whether the link goes with it can all be changed for this one
 * send, the same as an invoice's - the customer's own details are left alone.
 */
export function SendFeedbackRequestButton({ orderId }: { orderId: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button startIcon={<RateReviewIcon />} onClick={() => setOpen(true)}>
        Ask for feedback
      </Button>
      <SendFeedbackRequestDialog orderId={orderId} open={open} onClose={() => setOpen(false)} />
    </>
  )
}

function defaultSubject(contact: FeedbackContact) {
  return `Feedback on your ${contact.eventType ? contact.eventType.toLowerCase() : 'event'} (${contact.orderNumber})`
}

function defaultMessage(contact: FeedbackContact) {
  const event = contact.eventType ? contact.eventType.toLowerCase() : 'event'
  return `Dear ${contact.customerName},\n\nThank you for choosing us for your ${event}. We would value a minute of your time to tell us how it went.\n\nThank you,`
}

function SendFeedbackRequestDialog({
  orderId,
  open,
  onClose,
}: {
  orderId: string
  open: boolean
  onClose: () => void
}) {
  const contact = useFeedbackContact(orderId, open)
  const send = useSendFeedbackRequest()
  const [channels, setChannels] = useState<Channel[]>([])
  const [email, setEmail] = useState('')
  const [mobile, setMobile] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [includeLink, setIncludeLink] = useState(true)
  const [sent, setSent] = useState<FeedbackLinkSent[] | null>(null)

  // Filled from the customer once it arrives, and left alone after that: what
  // somebody has typed must not be overwritten by a refetch.
  useEffect(() => {
    const loaded = contact.data
    if (!loaded || !open) return
    setEmail(loaded.email ?? '')
    setMobile(loaded.mobile ?? '')
    setSubject(defaultSubject(loaded))
    setMessage(defaultMessage(loaded))
    setIncludeLink(true)
    setChannels(
      [
        loaded.consentEmail && loaded.email ? 'EMAIL' : null,
        loaded.consentWhatsapp && loaded.mobile ? 'WHATSAPP' : null,
      ].filter((value): value is Channel => value !== null),
    )
    setSent(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.data, open])

  const close = () => {
    setSent(null)
    send.reset()
    onClose()
  }

  const loaded = contact.data
  const canSend = Boolean(loaded && channels.length > 0 && message.trim()) && !send.isPending
  const toggle = (value: Channel) =>
    setChannels((current) => (current.includes(value) ? current.filter((one) => one !== value) : [...current, value]))
  const liveFor = (value: Channel) => (loaded ? (value === 'EMAIL' ? loaded.emailLive : loaded.whatsappLive) : true)
  const consentFor = (value: Channel) =>
    loaded ? (value === 'EMAIL' ? loaded.consentEmail : loaded.consentWhatsapp) : true

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Ask for feedback{loaded ? ` · ${loaded.orderNumber}` : ''}</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={send.error ?? contact.error} />
        {sent ? (
          <Stack spacing={2}>
            {sent.map((one) => (
              <Typography key={one.channel} variant="body2">
                Sent by {one.channel === 'EMAIL' ? 'email' : 'WhatsApp'} to {one.recipient}.
              </Typography>
            ))}
            <Typography variant="body2" color="text.secondary">
              Replies appear on the Feedback page under Finance &amp; experience.
            </Typography>
            <TextField
              label="Feedback link"
              value={sent[0].link}
              size="small"
              InputProps={{ readOnly: true }}
              helperText="Copy it to send by any other route."
            />
            <Button startIcon={<ContentCopyIcon />} onClick={() => void navigator.clipboard?.writeText(sent[0].link)}>
              Copy link
            </Button>
          </Stack>
        ) : contact.isLoading || !loaded ? (
          <InlineSpinner />
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              To {loaded.customerName} ({loaded.customerCode}).
            </Typography>

            <Stack direction="row" spacing={1}>
              <Chip
                icon={<EmailIcon />}
                label="Email"
                color={channels.includes('EMAIL') ? 'primary' : 'default'}
                variant={channels.includes('EMAIL') ? 'filled' : 'outlined'}
                onClick={() => toggle('EMAIL')}
                disabled={!consentFor('EMAIL')}
              />
              <Chip
                icon={<WhatsAppIcon />}
                label="WhatsApp"
                color={channels.includes('WHATSAPP') ? 'primary' : 'default'}
                variant={channels.includes('WHATSAPP') ? 'filled' : 'outlined'}
                onClick={() => toggle('WHATSAPP')}
                disabled={!consentFor('WHATSAPP')}
              />
            </Stack>

            {!loaded.consentEmail || !loaded.consentWhatsapp ? (
              <Alert severity="info">
                {loaded.customerName} has asked not to be contacted
                {!loaded.consentEmail ? ' by email' : ''}
                {!loaded.consentEmail && !loaded.consentWhatsapp ? ' or' : ''}
                {!loaded.consentWhatsapp ? ' on WhatsApp' : ''}.
              </Alert>
            ) : null}

            {channels.filter((value) => !liveFor(value)).map((value) => (
              <Alert key={value} severity="warning">
                {value === 'EMAIL' ? 'Email' : 'WhatsApp'} is not connected yet. The request will be recorded but
                will not reach the customer until the account is set up.
              </Alert>
            ))}

            {channels.includes('EMAIL') ? (
              <TextField
                fullWidth
                label="Email address"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                helperText="Sending to a different address does not change the customer's own"
              />
            ) : null}

            {channels.includes('WHATSAPP') ? (
              <TextField
                fullWidth
                label="Mobile number"
                value={mobile}
                onChange={(event) => setMobile(event.target.value)}
                helperText="Ten digits; the country code is added"
              />
            ) : null}

            {channels.includes('EMAIL') ? (
              <TextField fullWidth label="Subject" value={subject} onChange={(event) => setSubject(event.target.value)} />
            ) : null}

            <TextField
              fullWidth
              multiline
              minRows={5}
              label="Message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />

            <FormControlLabel
              control={<Checkbox checked={includeLink} onChange={(event) => setIncludeLink(event.target.checked)} />}
              label="Include a link the customer can open on a phone"
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{sent ? 'Done' : 'Cancel'}</Button>
        {!sent && (
          <Button
            variant="contained"
            disabled={!canSend}
            onClick={async () => {
              if (channels.length === 0) return
              const result = await send.mutateAsync({
                orderId,
                channels,
                email: channels.includes('EMAIL') ? email : undefined,
                mobile: channels.includes('WHATSAPP') ? mobile : undefined,
                subject: channels.includes('EMAIL') ? subject : undefined,
                message,
                includeLink,
              })
              setSent(result)
            }}
          >
            Send
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
